import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Building2, Warehouse } from "lucide-react";
import { PageHeader, GlassCard, GradButton, EmptyState, ConfirmDelete, TIP_ETIKET } from "@/components/erp";
import { FormDialog } from "@/components/FormDialog";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg } from "@/lib/api";

const LOK_TIP = ["fabrika", "depo", "ambalaj_deposu", "geri_donusum", "magaza"];
const DEPO_TIP = { hammadde: "Hammadde", yari_mamul: "Yarı Mamul", mamul: "Mamul", ambalaj: "Ambalaj", karantina: "Karantina", sevkiyat: "Sevkiyat" };

const lokFields = [
  { k: "ad", label: "Ad *", wide: true }, { k: "kod", label: "Kod" },
  { k: "tip", label: "Tip", type: "select", options: LOK_TIP.map((t) => ({ value: t, label: TIP_ETIKET[t] })) },
  { k: "adres", label: "Adres", wide: true }, { k: "aktif", label: "Aktif", type: "switch" },
];
const depoFields = [
  { k: "ad", label: "Depo adı *", wide: true }, { k: "kod", label: "Kod" },
  { k: "tip", label: "Depo tipi", type: "select", options: Object.entries(DEPO_TIP).map(([value, label]) => ({ value, label })) },
  { k: "aktif", label: "Aktif", type: "switch" },
];

export default function Lokasyonlar() {
  const { isYonetim } = useAuth();
  const [loks, setLoks] = useState([]);
  const [depolar, setDepolar] = useState([]);
  const [dlg, setDlg] = useState(null);

  const load = useCallback(async () => {
    const [l, d] = await Promise.all([api.get("/lokasyonlar"), api.get("/depolar")]);
    setLoks(l.data); setDepolar(d.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const kaydet = (path) => async (f) => {
    if (f.id) await api.put(`/${path}/${f.id}`, f); else await api.post(`/${path}`, f);
    toast.success("Kaydedildi"); load();
  };
  const sil = async (path, id) => {
    try { await api.delete(`/${path}/${id}`); toast.success("Silindi"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="lokasyonlar-page">
      <PageHeader eyebrow="Ana veri" title="Lokasyon & Depolar" desc="Fabrikalar, depolar, ambalaj depoları ve mağazalar. Her lokasyonun altında birden çok depo olabilir.">
        {isYonetim && <GradButton data-testid="lokasyon-ekle" onClick={() => setDlg({ t: "lok", v: { ad: "", kod: "", tip: "fabrika", adres: "", aktif: true } })}><Plus className="w-4 h-4" />Yeni lokasyon</GradButton>}
      </PageHeader>
      {loks.length === 0 ? <GlassCard><EmptyState text="Lokasyon yok." testid="lokasyon-bos" /></GlassCard> : (
        <div className="grid lg:grid-cols-2 gap-5">
          {loks.map((l, i) => (
            <GlassCard key={l.id} className="p-6 fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`lokasyon-kart-${l.kod || l.id}`}>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl grad-bg glow flex items-center justify-center shrink-0"><Building2 className="w-5 h-5 text-white" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2"><h3 className="font-head font-semibold text-lg truncate">{l.ad}</h3>{!l.aktif && <span className="text-[10px] text-muted-foreground">(pasif)</span>}</div>
                  <div className="text-xs text-muted-foreground">{TIP_ETIKET[l.tip]} · <span className="font-mono">{l.kod}</span>{l.adres ? ` · ${l.adres}` : ""}</div>
                </div>
                {isYonetim && <div className="flex">
                  <button data-testid={`lokasyon-duzenle-${l.kod}`} onClick={() => setDlg({ t: "lok", v: l })} className="p-2 rounded-lg text-muted-foreground hover:text-white hover:bg-white/10"><Pencil className="w-4 h-4" /></button>
                  <ConfirmDelete testid={`lokasyon-sil-${l.kod}`} onConfirm={() => sil("lokasyonlar", l.id)} />
                </div>}
              </div>
              <div className="mt-5 space-y-2">
                {depolar.filter((d) => d.lokasyon_id === l.id).map((d) => (
                  <div key={d.id} className="flex items-center gap-3 rounded-xl bg-white/[0.04] px-4 py-2.5" data-testid={`depo-${d.kod || d.id}`}>
                    <Warehouse className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm flex-1">{d.ad}</span>
                    <span className="text-[11px] text-muted-foreground">{DEPO_TIP[d.tip]}</span>
                    {isYonetim && <>
                      <button data-testid={`depo-duzenle-${d.kod}`} onClick={() => setDlg({ t: "depo", v: d })} className="p-1.5 rounded-lg text-muted-foreground hover:text-white"><Pencil className="w-3.5 h-3.5" /></button>
                      <ConfirmDelete testid={`depo-sil-${d.kod}`} onConfirm={() => sil("depolar", d.id)} />
                    </>}
                  </div>
                ))}
                {isYonetim && <button data-testid={`depo-ekle-${l.kod}`} onClick={() => setDlg({ t: "depo", v: { ad: "", kod: "", tip: "mamul", aktif: true, lokasyon_id: l.id } })}
                  className="text-sm inline-flex items-center gap-1.5 text-[var(--g1)] hover:brightness-125 pt-1"><Plus className="w-4 h-4" />Depo ekle</button>}
              </div>
            </GlassCard>
          ))}
        </div>
      )}
      {dlg?.t === "lok" && <FormDialog prefix="lokasyon" title={dlg.v.id ? "Lokasyonu düzenle" : "Yeni lokasyon"} fields={lokFields} initial={dlg.v} onSubmit={kaydet("lokasyonlar")} onClose={() => setDlg(null)} />}
      {dlg?.t === "depo" && <FormDialog prefix="depo" title={dlg.v.id ? "Depoyu düzenle" : "Yeni depo"} fields={depoFields} initial={dlg.v} onSubmit={kaydet("depolar")} onClose={() => setDlg(null)} />}
    </div>
  );
}
